import { useEffect, useState, useCallback, useRef } from "react";
import { getAllUsers } from "../../../shared/services/adminApi";
import { EditIcon } from "../../../shared/icons/Icons";
import UserEditModal from "./UserEditModal";

const UserList = () => {
    const USERS_PER_PAGE = 20; // Change this value to adjust pagination size
    
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [searchMode, setSearchMode] = useState(false);
    const debounceTimer = useRef(null);

    // Debounce search input
    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            setDebouncedSearch(search);
            if (search.trim()) {
                setSearchMode(true);
                setPage(1); // Reset to page 1 when searching
            } else {
                setSearchMode(false);
                setPage(1); // Reset to page 1 when clearing search
            }
        }, 300);

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [search]);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            // Always use USERS_PER_PAGE limit and current page
            // Backend handles search filtering
            const limit = USERS_PER_PAGE;
            const currentPage = page;
            
            const response = await getAllUsers(currentPage, limit, debouncedSearch);
            const data = response?.data?.data || response?.data || [];
            const total = response?.data?.pagination?.total || response?.data?.total || response?.total || data.length;
            setUsers(Array.isArray(data) ? data : []);
            setTotalUsers(total);
            setTotalPages(Math.ceil(total / USERS_PER_PAGE) || 1);
        } catch (error) {
            console.error("Error fetching users:", error);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [page, debouncedSearch, USERS_PER_PAGE]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleEditClick = (user) => {
        setSelectedUser(user);
        setShowEditModal(true);
    };

    const handleModalClose = () => {
        setShowEditModal(false);
        setSelectedUser(null);
    };

    const handleSaved = () => {
        fetchUsers();
    };

    // Backend handles search filtering - no client-side filtering needed
    const displayedUsers = users;
    const userCount = totalUsers;

    return (
        <div>
            <div className="admin-table-container">
                <div className="admin-table-header">
                    <h4>
                        {debouncedSearch ? (
                            <>Search Results ({userCount})</>
                        ) : (
                            <>All Users ({userCount})</>
                        )}
                    </h4>
                    <div className="admin-search-box">
                        <span className="search-icon">
                            <i className="fa fa-search"></i>
                        </span>
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="admin-loading">
                        <div className="spinner"></div>
                    </div>
                ) : displayedUsers.length === 0 ? (
                    <div className="admin-empty-state">
                        <div className="empty-icon">👥</div>
                        <p>{debouncedSearch ? "No users match your search" : "No users found"}</p>
                    </div>
                ) : (
                    <>
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Mobile</th>
                                    <th>Role</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedUsers.map((user, index) => (
                                    <tr key={user._id || user.id || index}>
                                        <td>
                                            {(page - 1) * USERS_PER_PAGE + index + 1}
                                        </td>
                                        <td style={{ fontWeight: 600 }}>
                                            {user.first_name} {user.last_name}
                                        </td>
                                        <td>{user.email}</td>
                                        <td>{user.mobile || "- - -"}</td>
                                        <td>
                                            <span className={`role-badge role-${user.role || user.user_type || "user"}`}>
                                                {user.role || user.user_type || "user"}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                className="admin-action-btn btn-edit"
                                                onClick={() => handleEditClick(user)}
                                                title="Edit User"
                                            >
                                                <EditIcon size={14} /> Edit
                                            </button>
                                            <button
                                                className="admin-action-btn btn-assign"
                                                onClick={() => window.location.href = `/admin/plans/assign?user=${user._id || user.id}`}
                                                title="Assign/Change Plan"
                                                style={{ marginLeft: '8px' }}
                                            >
                                                📋 Plan
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {(
                            <div className="admin-pagination">
                                <button
                                    disabled={page <= 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                >
                                    Previous
                                </button>
                                <span className="page-info">
                                    Page {page} of {totalPages}
                                </span>
                                <button
                                    disabled={page >= totalPages}
                                    onClick={() => setPage((p) => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {showEditModal && (
                <UserEditModal
                    show={showEditModal}
                    onClose={handleModalClose}
                    user={selectedUser}
                    onSaved={handleSaved}
                />
            )}
        </div>
    );
};

export default UserList;